import {useInfiniteQuery, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {AxiosError} from 'axios';
import {tail} from 'lodash';
import {ToastMessage} from '../components/toast';
import {
  addChatMedia,
  createEventComment,
  deleteChatMedia,
  deleteEventComment,
  fetchChatMedia,
  fetchEventComment,
  fetchEventComments,
  fetchFeaturedEventComments,
  fetchReplyMentionComments,
  helpfulComment,
  reportComment,
  updateEventComment,
} from '../endpoints/api';
import {User} from './useAuth';
import {EventRSVPStatus} from './useEvent';

const INTERVAL_MS = 5000; // 5 seconds

interface FetchCommentOptions {
  eventId: number;
  page?: string;
}

export function useFetchEventComments({eventId, page = '1'}: FetchCommentOptions) {
  return useQuery(['event_comments', eventId], () => fetchEventComments({eventId, page}), {
    enabled: typeof eventId === 'number',
    // Refetch the data every INTERVAL_MS
    refetchInterval: INTERVAL_MS,
  });
}

export function useFetchFeaturedEventComments({eventId, page = '1'}: FetchCommentOptions) {
  return useQuery(
    ['featured_event_comments', eventId],
    () => fetchFeaturedEventComments({eventId, page}),
    {
      enabled: typeof eventId === 'number',
      // Refetch the data every INTERVAL_MS
      refetchInterval: INTERVAL_MS,
    }
  );
}

export const useInfiniteFeaturedEventComments = (eventId: number, enabled: boolean = true) => {
  return useInfiniteQuery(
    ['infinite_featured_event_comments', eventId],
    ({pageParam = 1}) => fetchFeaturedEventComments({eventId, page: pageParam}),
    {
      getNextPageParam: (lastPage) => {
        const currentPage = lastPage.data.meta?.current_page || lastPage.data.current_page;
        const lastPageNum = lastPage.data.meta?.last_page || lastPage.data.last_page;
        if (currentPage !== lastPageNum) {
          return currentPage + 1;
        }
      },
      refetchInterval: INTERVAL_MS,
      enabled: enabled && typeof eventId === 'number',
    }
  );
};

export function useFetchRepliesAndMentions(eventId: number, enabled: boolean = true) {
  return useQuery(['event_comments', eventId], () => fetchReplyMentionComments({eventId}), {
    enabled: enabled && typeof eventId === 'number',
    // Refetch the data every INTERVAL_MS
    refetchInterval: INTERVAL_MS,
  });
}

export function useFetchEventComment({eventId, commentId, enabled}) {
  return useQuery(
    ['event_comment', eventId, commentId],
    () => fetchEventComment({eventId, commentId}),
    {
      enabled: enabled && typeof eventId === 'number',
      // Refetch the data every INTERVAL_MS
      refetchInterval: INTERVAL_MS,
      retry: 1,
    }
  );
}

export const useInfiniteEventComments = (eventId: number, enabled: boolean = true) => {
  return useInfiniteQuery(
    ['infinite_event_comments', eventId],
    ({pageParam = 1}) => fetchEventComments({eventId, page: pageParam}),
    {
      getNextPageParam: (lastPage) => {
        const currentPage = lastPage.data.meta?.current_page || lastPage.data.current_page;
        const lastPageNum = lastPage.data.meta?.last_page || lastPage.data.last_page;
        if (currentPage !== lastPageNum) {
          return currentPage + 1;
        }
      },
      refetchInterval: INTERVAL_MS,
      enabled: enabled && typeof eventId === 'number',
    }
  );
};

export const useCreateEventComment = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      eventId,
      content,
      parent_id,
      is_featured,
      user,
      rsvp_status,
      hasImage = false,
    }: {
      eventId: number;
      content: string;
      parent_id?: string;
      is_featured?: boolean;
      user?: User;
      rsvp_status?: EventRSVPStatus;
      hasImage?: boolean;
    }) => createEventComment({eventId, content, parent_id, is_featured}),
    {
      onMutate: async (newComment) => {
        // Cancel any outgoing refetches
        // (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({
          queryKey: ['infinite_event_comments', newComment.eventId],
        });

        // Snapshot the previous value
        const previousComments: any = queryClient.getQueryData([
          'infinite_event_comments',
          newComment.eventId,
        ]);
        if (previousComments && previousComments.pages[0]) {
          // If this is a reply, find the parent comment to attach
          let parentComment = null;
          if (newComment.parent_id) {
            for (const page of previousComments.pages) {
              parentComment = page.data.data.find((c: any) => c.id === newComment.parent_id);
              if (parentComment) break;
            }
          }

          const newData = {
            ...previousComments,
            pages: [
              {
                ...previousComments.pages[0],
                data: {
                  ...previousComments.pages[0].data,
                  data: [
                    {
                      hasImage: true,
                      id: `temp-${Date.now()}`,
                      ...newComment,
                      user_id: newComment.user?.id,
                      parent: parentComment,
                    },
                    ...previousComments.pages[0].data.data,
                  ],
                },
              },
              ...tail(previousComments.pages),
            ],
          };

          // Optimistically update to the new value
          queryClient.setQueryData(['infinite_event_comments', newComment.eventId], newData);
        }

        // Return a context with the previous and new comment
        return {previousComments, newComment};
      },
      onSuccess: (_newComment, variables) => {
        queryClient.invalidateQueries(['infinite_event_comments', variables.eventId]);
      },
      onSettled: (newComment, error, variables, context) => {
        if (error && error instanceof AxiosError) {
          ToastMessage({
            status: 'error',
            text: error.message,
          });
        }
        //queryClient.invalidateQueries(['infinite_event_comments', variables.eventId]);
        //queryClient.invalidateQueries(['event_comments', variables.eventId]);
      },
    }
  );
};

export const useUpdateEventComment = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({eventId, commentId, content}: {eventId: number; commentId: string; content: string}) =>
      updateEventComment({eventId, commentId, content}),
    {
      onMutate: async (updatedComment) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({
          queryKey: ['infinite_event_comments', updatedComment.eventId],
        });
        await queryClient.cancelQueries({
          queryKey: ['event_comments', updatedComment.eventId],
        });

        // Snapshot the previous value
        const previousComments: any = queryClient.getQueryData([
          'infinite_event_comments',
          updatedComment.eventId,
        ]);
        const previousRepliesMentions: any = queryClient.getQueryData([
          'event_comments',
          updatedComment.eventId,
        ]);

        if (previousComments && previousComments.pages) {
          // Update the comment and any children that reference it as parent
          const newData = {
            ...previousComments,
            pages: previousComments.pages.map((page: any) => ({
              ...page,
              data: {
                ...page.data,
                data: page.data.data.map((comment: any) => {
                  // Update the comment itself
                  if (comment.id === updatedComment.commentId) {
                    return {...comment, content: updatedComment.content};
                  }
                  // Update parent reference in child comments
                  if (comment.parent && comment.parent.id === updatedComment.commentId) {
                    return {
                      ...comment,
                      parent: {...comment.parent, content: updatedComment.content},
                    };
                  }
                  return comment;
                }),
              },
            })),
          };

          queryClient.setQueryData(['infinite_event_comments', updatedComment.eventId], newData);
        }

        // Also update replies and mentions cache
        if (previousRepliesMentions?.data) {
          const updateCommentInArray = (comments: any[]) => 
            comments.map((comment: any) => {
              if (comment.id === updatedComment.commentId) {
                return {...comment, content: updatedComment.content};
              }
              if (comment.parent && comment.parent.id === updatedComment.commentId) {
                return {
                  ...comment,
                  parent: {...comment.parent, content: updatedComment.content},
                };
              }
              return comment;
            });

          const newRepliesMentions = {
            ...previousRepliesMentions,
            data: {
              ...previousRepliesMentions.data,
              replies: updateCommentInArray(previousRepliesMentions.data.replies || []),
              mentions: updateCommentInArray(previousRepliesMentions.data.mentions || []),
            },
          };

          queryClient.setQueryData(['event_comments', updatedComment.eventId], newRepliesMentions);
        }

        return {previousComments, previousRepliesMentions};
      },
      onSettled: (newComment, error, variables, context) => {
        if (error && error instanceof AxiosError) {
          ToastMessage({
            status: 'error',
            text: error.message,
          });
        }
        queryClient.invalidateQueries(['infinite_event_comments', variables.eventId]);
        queryClient.invalidateQueries(['event_comments', variables.eventId]);
      },
    }
  );
};

export const useDeleteEventComment = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({eventId, commentId}: {eventId: number; commentId: string}) =>
      deleteEventComment({eventId, commentId}),
    {
      onMutate: async (variables) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries(['infinite_event_comments', variables.eventId]);
        await queryClient.cancelQueries(['event_comments', variables.eventId]);

        // Snapshot the previous values
        const previousInfiniteComments = queryClient.getQueryData(['infinite_event_comments', variables.eventId]);
        const previousComments = queryClient.getQueryData(['event_comments', variables.eventId]);

        // Optimistically mark the comment as deleted in infinite query
        queryClient.setQueryData(['infinite_event_comments', variables.eventId], (old: any) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              data: {
                ...page.data,
                data: page.data.data.map((comment: any) => 
                  comment.id === variables.commentId 
                    ? {...comment, content: 'Message deleted by author', status: 0} 
                    : comment
                ),
              },
            })),
          };
        });

        // Optimistically mark the comment as deleted in regular query
        queryClient.setQueryData(['event_comments', variables.eventId], (old: any) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              data: {
                ...page.data,
                data: page.data.data.map((comment: any) => 
                  comment.id === variables.commentId 
                    ? {...comment, content: 'Message deleted by author', status: 0} 
                    : comment
                ),
              },
            })),
          };
        });

        return {previousInfiniteComments, previousComments};
      },
      onError: (error, variables, context: any) => {
        // Rollback on error
        if (context?.previousInfiniteComments) {
          queryClient.setQueryData(['infinite_event_comments', variables.eventId], context.previousInfiniteComments);
        }
        if (context?.previousComments) {
          queryClient.setQueryData(['event_comments', variables.eventId], context.previousComments);
        }
        if (error && error instanceof AxiosError) {
          ToastMessage({
            status: 'error',
            text: error.message,
          });
        }
      },
      onSettled: (newComment, error, variables, context) => {
        queryClient.invalidateQueries(['infinite_event_comments', variables.eventId]);
        queryClient.invalidateQueries(['event_comments', variables.eventId]);
      },
    }
  );
};

export const useReportComment = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({eventId, commentId}: {eventId: number; commentId: string}) =>
      reportComment({eventId, commentId}),
    {
      onMutate: async (variables) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries(['infinite_event_comments', variables.eventId]);
        await queryClient.cancelQueries(['event_comments', variables.eventId]);
        await queryClient.cancelQueries(['infinite_featured_event_comments', variables.eventId]);

        // Snapshot the previous values
        const previousInfiniteComments = queryClient.getQueryData(['infinite_event_comments', variables.eventId]);
        const previousComments = queryClient.getQueryData(['event_comments', variables.eventId]);
        const previousFeaturedComments = queryClient.getQueryData(['infinite_featured_event_comments', variables.eventId]);

        // Optimistically remove the comment from infinite query
        queryClient.setQueryData(['infinite_event_comments', variables.eventId], (old: any) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              data: {
                ...page.data,
                data: page.data.data.filter((comment: any) => comment.id !== variables.commentId),
              },
            })),
          };
        });

        // Optimistically remove from replies and mentions
        queryClient.setQueryData(['event_comments', variables.eventId], (old: any) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: {
              ...old.data,
              replies: old.data.replies?.filter((comment: any) => comment.id !== variables.commentId) || [],
              mentions: old.data.mentions?.filter((comment: any) => comment.id !== variables.commentId) || [],
            },
          };
        });

        // Optimistically remove from featured comments
        queryClient.setQueryData(['infinite_featured_event_comments', variables.eventId], (old: any) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              data: {
                ...page.data,
                data: page.data.data.filter((comment: any) => comment.id !== variables.commentId),
              },
            })),
          };
        });

        return {previousInfiniteComments, previousComments, previousFeaturedComments};
      },
      onError: (error, variables, context: any) => {
        // Rollback on error
        if (context?.previousInfiniteComments) {
          queryClient.setQueryData(['infinite_event_comments', variables.eventId], context.previousInfiniteComments);
        }
        if (context?.previousComments) {
          queryClient.setQueryData(['event_comments', variables.eventId], context.previousComments);
        }
        if (context?.previousFeaturedComments) {
          queryClient.setQueryData(['infinite_featured_event_comments', variables.eventId], context.previousFeaturedComments);
        }
        if (error && error instanceof AxiosError) {
          ToastMessage({
            status: 'error',
            text: error.message,
          });
        }
      },
      onSettled: (newComment, error, variables, context) => {
        queryClient.invalidateQueries(['infinite_event_comments', variables.eventId]);
        queryClient.invalidateQueries(['event_comments', variables.eventId]);
        queryClient.invalidateQueries(['infinite_featured_event_comments', variables.eventId]);
      },
    }
  );
};

export const useHelpfulComment = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({eventId, commentId}: {eventId: number; commentId: string}) =>
      helpfulComment({eventId, commentId}),
    {
      onSettled: (newComment, error, variables, context) => {
        queryClient.invalidateQueries(['infinite_event_comments', variables.eventId]);
        queryClient.invalidateQueries(['event_comments', variables.eventId]);
      },
    }
  );
};

export const useAddCommentImage = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      formData,
      target_id,
      comment_id,
    }: {
      formData: FormData;
      target_id: string;
      comment_id: string;
    }) =>
      addChatMedia({
        formData,
        target_type: 'event',
        target_id,
        comment_type: 'event_comment',
        comment_id,
      }),
    {
      onSettled: (newComment, error, variables, context) => {
        if (error && error instanceof AxiosError) {
          ToastMessage({
            status: 'error',
            text: error.message,
          });
        }
        queryClient.invalidateQueries(['infinite_event_comments', variables.target_id]);
        queryClient.invalidateQueries(['event_comments', variables.target_id]);
      },
    }
  );
};

export const useDeleteCommentImage = () => {
  const queryClient = useQueryClient();
  return useMutation(({mediaId}: {mediaId: string}) => deleteChatMedia({mediaId}), {
    onSettled: (newComment, error, variables, context) => {
      if (error && error instanceof AxiosError) {
        ToastMessage({
          status: 'error',
          text: error.message,
        });
      }
      queryClient.invalidateQueries(['infinite_event_comments']);
      queryClient.invalidateQueries(['event_comments']);
    },
  });
};

export const useFetchCommentMedia = ({mediaId}) => {
  return useQuery(['event_comment_media', mediaId], () => fetchChatMedia({mediaId}), {
    enabled: typeof mediaId === 'number',
  });
};
